import { useState, useEffect, useCallback } from 'react';
import { Phone, Settings, Search, Ticket, ClipboardList, Play, Loader2, Copy, RefreshCw, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';
const fmtDur = (s) => s == null ? '-' : `${Math.floor(s / 60)}m ${s % 60}s`;

const SYSTEM_PROMPT = `Je bent de virtuele assistent van Solar Engineering, een Belgisch bedrijf gespecialiseerd in zonnepanelen, thuisbatterijen, laadpalen en reiniging/onderhoud van PV-installaties.

TAAL: Spreek Vlaams-Nederlands (beleefd, warm, professioneel). Als de beller 2 drukte spreek je Frans, bij 3 Engels.

OPENING: "Hallo, ik ben de virtuele assistent van Solar Engineering. Hoe kan ik u vandaag helpen?"

WAT JE KAN:
1. Vragen beantwoorden over onze diensten (zonnepanelen, thuisbatterijen, laadpalen, reiniging, onderhoud, keuring).
2. Een TICKET aanmaken bij problemen/defecten/vragen van bestaande klanten (tool: create_ticket). Vraag altijd: naam, beschrijving van het probleem, en bevestig het telefoonnummer.
3. Een AANVRAAG (offerte/lead) registreren voor nieuwe klanten (tool: create_aanvraag). Vraag: voornaam, achternaam, telefoonnummer, e-mail, adres (straat, nr, postcode, gemeente), en waarover het gaat (zonnepanelen/batterij/laadpaal/reiniging).
4. Klant opzoeken op telefoonnummer (tool: lookup_customer) om te personaliseren.

REGELS:
- Zeg bij de start van acties wat je doet ("Ik noteer dat even voor u...").
- Herhaal naam, nummer en e-mail ter bevestiging vóór je een tool gebruikt.
- Beloof NOOIT prijzen, kortingen of exacte datums. Zeg dat een collega contact opneemt.
- Bij dringende veiligheidsproblemen (brandgeur, rook): adviseer installatie uitschakelen en noodnummer 112 bij gevaar; maak een URGENT ticket.
- Hou het gesprek kort en vriendelijk. Sluit af met: "Bedankt voor uw oproep, een fijne dag nog!"
- Je bent een AI-assistent en zegt dat eerlijk als ernaar gevraagd wordt.`;

export default function AIPhone() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ elevenlabs_api_key: '', agent_id: '', webhook_secret: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/ai-phone/calls', { params: search ? { search } : {} });
      setCalls(res.data || []);
    } catch { toast.error('Kon gesprekken niet laden'); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const openSettings = async () => {
    try {
      const res = await api.get('/ai-phone/settings');
      setSettings(res.data);
      setForm({ elevenlabs_api_key: '', agent_id: res.data.agent_id || '', webhook_secret: res.data.webhook_secret || '' });
      setSettingsOpen(true);
    } catch { toast.error('Kon instellingen niet laden'); }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const payload = { agent_id: form.agent_id, webhook_secret: form.webhook_secret };
      if (form.elevenlabs_api_key) payload.elevenlabs_api_key = form.elevenlabs_api_key;
      await api.put('/ai-phone/settings', payload);
      toast.success('Instellingen opgeslagen');
      setSettingsOpen(false);
    } catch { toast.error('Kon instellingen niet opslaan'); }
    finally { setSaving(false); }
  };

  const openDetail = async (call) => {
    setDetailLoading(true);
    setAudioUrl(null);
    setDetail({ ...call, transcript: [] });
    try {
      const res = await api.get(`/ai-phone/calls/${call.id}`);
      setDetail(res.data);
    } catch { toast.error('Kon gesprek niet laden'); }
    finally { setDetailLoading(false); }
  };

  const loadAudio = async () => {
    if (!detail) return;
    setAudioLoading(true);
    try {
      const res = await api.get(`/ai-phone/calls/${detail.id}/audio`, { responseType: 'blob' });
      setAudioUrl(URL.createObjectURL(res.data));
    } catch (e) {
      toast.error(e?.response?.status === 400 ? 'Stel eerst de ElevenLabs API key in' : 'Audio niet beschikbaar');
    } finally { setAudioLoading(false); }
  };

  const deleteCall = async (e, call) => {
    e.stopPropagation();
    if (!window.confirm('Dit gesprek definitief verwijderen?')) return;
    try {
      await api.delete(`/ai-phone/calls/${call.id}`);
      setCalls(prev => prev.filter(c => c.id !== call.id));
      toast.success('Gesprek verwijderd');
    } catch { toast.error('Kon gesprek niet verwijderen'); }
  };

  const copy = (text, label) => { navigator.clipboard.writeText(text); toast.success(`${label} gekopieerd`); };
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const today = new Date().toDateString();
  const stats = {
    totaal: calls.length,
    vandaag: calls.filter(c => new Date(c.started_at).toDateString() === today).length,
    tickets: calls.reduce((a, c) => a + (c.actions || []).filter(x => x.type === 'ticket').length, 0),
    aanvragen: calls.reduce((a, c) => a + (c.actions || []).filter(x => x.type === 'aanvraag').length, 0),
  };

  return (
    <div className="space-y-6" data-testid="ai-phone-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Phone className="w-6 h-6" /> AI Telefoon</h1>
          <p className="text-sm text-muted-foreground">Gesprekken van de virtuele telefoonassistent</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="ai-phone-refresh-btn"><RefreshCw className="w-4 h-4 mr-1" /> Vernieuwen</Button>
          {isAdmin && (
            <Button size="sm" onClick={openSettings} data-testid="ai-phone-settings-btn"><Settings className="w-4 h-4 mr-1" /> Instellingen & setup</Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[['Gesprekken', stats.totaal], ['Vandaag', stats.vandaag], ['Tickets aangemaakt', stats.tickets], ['Aanvragen aangemaakt', stats.aanvragen]].map(([l, v]) => (
          <Card key={l}><CardContent className="p-4"><p className="text-xs text-muted-foreground">{l}</p><p className="text-2xl font-bold">{v}</p></CardContent></Card>
        ))}
      </div>

      <div className="relative max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input className="pl-9" placeholder="Zoek op nummer, klant of inhoud..." value={search} onChange={e => setSearch(e.target.value)} data-testid="ai-phone-search" />
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : calls.length === 0 ? (
        <Card><CardContent className="p-10 text-center space-y-2">
          <Phone className="w-10 h-10 mx-auto text-muted-foreground" />
          <p className="font-medium">Nog geen gesprekken</p>
          <p className="text-sm text-muted-foreground">Zodra de AI-assistent een oproep beantwoordt, verschijnt het volledige gespreksdossier hier.{isAdmin && ' Open "Instellingen & setup" om de koppeling met ElevenLabs te configureren.'}</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {calls.map(c => (
            <Card key={c.id} className="cursor-pointer hover:bg-accent/40 transition-colors" onClick={() => openDetail(c)} data-testid={`ai-call-${c.id}`}>
              <CardContent className="p-4 flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{c.customer_name || c.caller_name || c.caller_number || 'Onbekende beller'}</span>
                    {(c.customer_name || c.caller_name) && c.caller_number && <span className="text-xs text-muted-foreground">{c.caller_number}</span>}
                    {(c.actions || []).some(a => a.type === 'ticket') && <Badge variant="secondary"><Ticket className="w-3 h-3 mr-1" />Ticket</Badge>}
                    {(c.actions || []).some(a => a.type === 'aanvraag') && <Badge variant="secondary"><ClipboardList className="w-3 h-3 mr-1" />Aanvraag</Badge>}
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{c.summary || 'Geen samenvatting'}</p>
                </div>
                <div className="flex items-center gap-1 text-right text-xs text-muted-foreground whitespace-nowrap">
                  <div>
                    <p>{fmtDate(c.started_at)}</p>
                    <p>{fmtDur(c.duration_secs)}</p>
                  </div>
                  {isAdmin && (
                    <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={(e) => deleteCall(e, c)} data-testid={`delete-call-${c.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) { setDetail(null); setAudioUrl(null); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Gesprek — {detail?.customer_name || detail?.caller_name || detail?.caller_number || 'Onbekend'}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="flex gap-4 text-sm text-muted-foreground flex-wrap">
                <span>{fmtDate(detail.started_at)}</span><span>Duur: {fmtDur(detail.duration_secs)}</span>
                {detail.language && <span>Taal: {detail.language}</span>}
              </div>
              {(detail.actions || []).length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {detail.actions.map(a => (
                    <Badge key={a.id} variant="outline">{a.type === 'ticket' ? <Ticket className="w-3 h-3 mr-1" /> : <ClipboardList className="w-3 h-3 mr-1" />}{a.title}</Badge>
                  ))}
                </div>
              )}
              {detail.summary && (
                <div className="bg-accent/40 rounded-lg p-3">
                  <p className="text-xs font-semibold uppercase text-muted-foreground mb-1">Samenvatting</p>
                  <p className="text-sm whitespace-pre-wrap">{detail.summary}</p>
                </div>
              )}
              <div>
                {audioUrl ? (
                  <audio controls src={audioUrl} className="w-full" data-testid="ai-call-audio" />
                ) : (
                  <Button variant="outline" size="sm" onClick={loadAudio} disabled={audioLoading} data-testid="ai-call-load-audio-btn">
                    {audioLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />} Opname beluisteren
                  </Button>
                )}
              </div>
              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground mb-2">Transcript</p>
                {detailLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                  <div className="space-y-2">
                    {(detail.transcript || []).map((t, i) => (
                      <div key={i} className={`flex ${t.role === 'agent' ? 'justify-start' : 'justify-end'}`}>
                        <div className={`rounded-lg px-3 py-2 text-sm max-w-[85%] ${t.role === 'agent' ? 'bg-accent' : 'bg-primary text-primary-foreground'}`}>
                          <p className="text-[10px] opacity-70 mb-0.5">{t.role === 'agent' ? 'AI-assistent' : 'Beller'}</p>
                          {t.message}
                        </div>
                      </div>
                    ))}
                    {!detail.transcript?.length && <p className="text-sm text-muted-foreground">Geen transcript beschikbaar</p>}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Settings dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>AI Telefoon — Instellingen & setup</DialogTitle></DialogHeader>
          {settings && (
            <div className="space-y-5">
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium">ElevenLabs API key {settings.has_api_key && <span className="text-xs text-muted-foreground">(ingesteld: {settings.elevenlabs_api_key_masked})</span>}</label>
                  <Input type="password" placeholder={settings.has_api_key ? 'Laat leeg om te behouden' : 'xi-...'} value={form.elevenlabs_api_key} onChange={e => setForm(f => ({ ...f, elevenlabs_api_key: e.target.value }))} data-testid="ai-settings-apikey" />
                </div>
                <div>
                  <label className="text-sm font-medium">Agent ID</label>
                  <Input placeholder="agent_..." value={form.agent_id} onChange={e => setForm(f => ({ ...f, agent_id: e.target.value }))} data-testid="ai-settings-agentid" />
                </div>
                <div>
                  <label className="text-sm font-medium">Webhook secret (uit ElevenLabs webhook-configuratie)</label>
                  <Input placeholder="wsec_..." value={form.webhook_secret} onChange={e => setForm(f => ({ ...f, webhook_secret: e.target.value }))} data-testid="ai-settings-webhooksecret" />
                </div>
                <Button onClick={saveSettings} disabled={saving} data-testid="ai-settings-save-btn">{saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Opslaan</Button>
              </div>

              <div className="border rounded-lg p-4 space-y-3 text-sm">
                <p className="font-semibold">Setup-gids (eenmalig)</p>
                <ol className="list-decimal pl-5 space-y-2">
                  <li>Maak een account op <b>elevenlabs.io</b> (plan met Agents). Ga naar <b>Agents → New agent</b>.</li>
                  <li>Kies een <b>Vlaamse stem</b> (Voice Library → zoek "Flemish" of "Vlaams", bv. een Belgisch-Nederlandse stem) en taal Nederlands.</li>
                  <li>Zet als <b>First message</b>: "Voor Nederlands, druk 1. Pour le français, appuyez sur 2. For English, press 3." en activeer <b>keypad touch tones (DTMF)</b> bij de agent-instellingen. Na de keuze begroet de agent: "Hallo, ik ben de virtuele assistent van Solar Engineering…"</li>
                  <li>Plak onderstaande <b>systeemprompt</b> in de agent.</li>
                  <li>Voeg 3 <b>server tools (webhooks)</b> toe met telkens header <code>X-Tool-Secret</code> = de tool secret hieronder:
                    <ul className="list-disc pl-5 mt-1 space-y-1">
                      <li><b>lookup_customer</b> → POST {backendUrl}/api/ai-phone/tools/lookup-customer (body: phone, conversation_id)</li>
                      <li><b>create_ticket</b> → POST {backendUrl}/api/ai-phone/tools/create-ticket (body: subject, description, priority, caller_phone, caller_name, conversation_id)</li>
                      <li><b>create_aanvraag</b> → POST {backendUrl}/api/ai-phone/tools/create-aanvraag (body: first_name, last_name, phone, email, street, number, postal_code, city, request_type, message, conversation_id)</li>
                    </ul>
                    Geef bij elke tool de dynamic variable <code>system__conversation_id</code> mee als <code>conversation_id</code> en <code>system__caller_id</code> als telefoonnummer.
                  </li>
                  <li>Koppel een <b>telefoonnummer</b> aan de agent (Agents → Phone numbers → importeer via Twilio of SIP-trunk).</li>
                  <li>Zet bij ElevenLabs → Agents → Settings → <b>Post-call webhook</b>: URL hieronder; kopieer de getoonde secret in het veld hierboven. Zet ook "audio opslaan" aan voor opnames.</li>
                  <li>Doorschakelen na 5s op jouw gsm (Proximus/Orange/Telenet): bel <code>**61*AGENTNUMMER**11*05#</code> (05 = 5 seconden). Uitschakelen: <code>##61#</code>.</li>
                </ol>
                <div className="space-y-2">
                  {[
                    ['Webhook URL', `${backendUrl}/api/webhooks/elevenlabs/post-call`],
                    ['Tool secret (X-Tool-Secret header)', settings.tool_secret],
                  ].map(([l, v]) => (
                    <div key={l} className="flex items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-muted-foreground">{l}</p>
                        <p className="text-xs font-mono truncate">{v}</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => copy(v, l)}><Copy className="w-3.5 h-3.5" /></Button>
                    </div>
                  ))}
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground">Systeemprompt (Vlaams, met tools)</p>
                      <p className="text-xs font-mono line-clamp-2">{SYSTEM_PROMPT}</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => copy(SYSTEM_PROMPT, 'Systeemprompt')} data-testid="copy-system-prompt"><Copy className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
